import { IonRow, IonGrid, IonCol, IonDatetimeButton, IonModal, IonDatetime } from '@ionic/react';
import { filter, find, map } from 'lodash/fp';
import { DateTime, Interval } from 'luxon';
import { forwardRef, useImperativeHandle, useRef } from 'react';

import './DayPicker.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { dateFromOdoo } from '../utils/date';
import { useContracts, useGroupedPointing, usePublicHolidays, useResourceCalendarLines } from '../hooks/useSwr';

enum ColorByStateEnum {
    HasPointing = 'green',
    HasNoPointing = 'orange',
    DayOff = 'gray',
    PublicHoliday = 'gray',
    Disabled = 'gray',
    FutureDay = 'black',
}

export type DayPickerHandle = {
    refresh: () => void;
};

const DayPicker: React.ForwardRefRenderFunction<
    DayPickerHandle,
    {
        date: DateTime<true>;
        employees: number[];
        disabled?: boolean;
        onChange: (date: DateTime) => void;
    }
> = ({ date, employees, disabled, onChange }, ref) => {
    const weekDatetimes = Interval.fromDateTimes(date.startOf('week'), date.endOf('week'))
        .splitBy({ day: 1 })
        .map((d) => d.start!.setLocale('fr'));

    const { data: groupedPointing, mutate } = useGroupedPointing(employees, disabled, date);

    const { data: resourceCalendarLines } = useResourceCalendarLines();
    const { data: publicHolidays } = usePublicHolidays(date);
    const { map: mapContracts } = useContracts(employees);

    const contractCalendarIds = mapContracts((e) => e.resource_calendar_id[0]);
    const ressourceCalendar = filter(
        ({ calendar_id }) => contractCalendarIds.includes(calendar_id),
        resourceCalendarLines
    );

    const getTextColor = (day: DateTime, ingoreDisabled?: boolean): ColorByStateEnum => {
        if (disabled && !ingoreDisabled) {
            return ColorByStateEnum.Disabled;
        }
        if (groupedPointing.find((p) => dateFromOdoo(p['date:day']).hasSame(day, 'day'))) {
            return ColorByStateEnum.HasPointing;
        }

        if (publicHolidays.find((e) => e.date.hasSame(day, 'day'))) {
            return ColorByStateEnum.PublicHoliday;
        }

        if (!find({ dayofweek: day.weekday - 1 }, ressourceCalendar)) {
            return ColorByStateEnum.DayOff;
        }
        return day > DateTime.now() ? ColorByStateEnum.FutureDay : ColorByStateEnum.HasNoPointing;
    };

    useImperativeHandle(
        ref,
        () => ({
            refresh: () => mutate(),
        }),
        [mutate]
    );

    return (
        <IonGrid className="mt-4 bg-gray-50 items-center day-picker-container">
            <IonRow className="ion-align-items-center">
                <IonCol size="1" className="text-left" onClick={() => !disabled && onChange(date.minus({ week: 1 }))}>
                    <FontAwesomeIcon icon={faChevronLeft} className={disabled ? 'text-gray-400' : 'text-[#3880ff]'} />
                </IonCol>
                <IonCol size="10" className="text-center">
                    <DateWithPicker date={date} employees={employees} disabled={disabled} onChange={onChange} />
                </IonCol>
                <IonCol
                    size="1"
                    className="text-right align-middle"
                    onClick={() => !disabled && onChange(date.plus({ week: 1 }))}
                >
                    <FontAwesomeIcon icon={faChevronRight} className={disabled ? 'text-gray-400' : 'text-[#3880ff]'} />
                </IonCol>
            </IonRow>
            <IonRow>
                {map(
                    (day) => (
                        <IonCol key={day.toMillis()}>
                            <button
                                className="flex items-center flex-col w-full"
                                onClick={() => {
                                    if (!disabled) onChange(day);
                                }}
                            >
                                <span
                                    className={`${day.hasSame(DateTime.now(), 'day') ? 'font-bold text-red-600' : ''}`}
                                >
                                    {day.toFormat('ccccc')}
                                </span>
                                <span
                                    className={`picker-badge h-7 w-7 leading-7 rounded-full font-bold ${getTextColor(
                                        day
                                    )} ${day.hasSame(date, 'day') ? ' selected' : ''}`}
                                >
                                    {day.toFormat('d')}
                                </span>
                            </button>
                        </IonCol>
                    ),
                    weekDatetimes
                )}
            </IonRow>
        </IonGrid>
    );
};

const DateWithPicker: React.FC<{
    date: DateTime;
    employees: number[];
    disabled?: boolean;
    onChange: (date: DateTime) => void;
}> = ({ date, employees, disabled, onChange }) => {
    const calendarRef = useRef<HTMLIonDatetimeElement>(null);

    return (
        <>
            <IonDatetimeButton datetime="day-picker-datetime" disabled={disabled}>
                <span slot="date-target">{date.toFormat('cccc d LLLL yyyy')}</span>
            </IonDatetimeButton>

            <IonModal keepContentsMounted={true}>
                <IonDatetime
                    id="day-picker-datetime"
                    ref={calendarRef}
                    presentation="date"
                    value={date.toISO()}
                    onIonChange={(e) => onChange(DateTime.fromISO(e.detail.value as string))}
                    doneText="Valider"
                    showDefaultButtons={true}
                    cancelText="Annuler"
                    locale="FR-fr"
                    firstDayOfWeek={1}
                />
            </IonModal>
        </>
    );
};

export default forwardRef(DayPicker);
